import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DOC_STATUSES } from "@/lib/roles";
import { fmtDate, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

export default function Engineering() {
  const { projectId, canWrite } = useProjectCtx();
  const canEdit = canWrite("ENGINEERING");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/engineering`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  const leafItems = items.filter((i) => !i.is_group);

  return (
    <div className="space-y-4" data-testid="engineering-page">
      <PageHeader title="Engineering — Drawing Status" description={canEdit ? "You can submit and approve drawings." : "Read-only for your project role."} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && leafItems.length === 0 ? (
          <EmptyState title="No engineering items" description="Add drawings under WBS & Weightages first." testId="engineering-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Drawing</TableHead><TableHead>Discipline</TableHead><TableHead>Target</TableHead><TableHead>Status</TableHead><TableHead>Submitted</TableHead><TableHead>Approved</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {leafItems.map((i) => (
                <TableRow key={i.id} data-testid={`engineering-row-${i.id}`}>
                  <TableCell className="font-mono text-xs">{i.code}</TableCell>
                  <TableCell className="text-foreground">{i.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{i.engineering?.discipline || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.engineering?.target_date)}</TableCell>
                  <TableCell><StatusPill value={i.engineering?.status} /></TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.engineering?.submitted_date)}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.engineering?.approved_date)}</TableCell>
                  <TableCell className="text-right">
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`edit-engineering-btn-${i.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {editing && <EngineeringDialog projectId={projectId} item={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function EngineeringDialog({ projectId, item, onClose, onSaved }) {
  const d = item.engineering || {};
  const [form, setForm] = useState({
    target_date: d.target_date || "", status: d.status || "NOT_STARTED", submitted_date: d.submitted_date || "",
    approved_date: d.approved_date || "", revision: d.revision || "", discipline: d.discipline || "",
    critical_for_construction: d.critical_for_construction || false, remarks: d.remarks || "", entry_date: todayISO(),
  });
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async () => {
    try {
      await api.patch(`/projects/${projectId}/engineering/${item.id}`, {
        ...form,
        target_date: form.target_date || null, submitted_date: form.submitted_date || null, approved_date: form.approved_date || null,
      });
      toast.success("Engineering status updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="engineering-dialog">
        <DialogHeader><DialogTitle>{item.code} — {item.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Target date</Label><Input type="date" data-testid="eng-target-date-input" value={form.target_date} onChange={set("target_date")} className="mt-1.5" /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="eng-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Submitted date</Label><Input type="date" data-testid="eng-submitted-date-input" value={form.submitted_date} onChange={set("submitted_date")} className="mt-1.5" /></div>
            <div><Label>Approved date</Label><Input type="date" data-testid="eng-approved-date-input" value={form.approved_date} onChange={set("approved_date")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Revision</Label><Input data-testid="eng-revision-input" value={form.revision} onChange={set("revision")} className="mt-1.5" /></div>
            <div><Label>Discipline</Label><Input data-testid="eng-discipline-input" value={form.discipline} onChange={set("discipline")} className="mt-1.5" /></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={form.critical_for_construction} onCheckedChange={(v) => setForm({ ...form, critical_for_construction: v })} data-testid="eng-critical-switch" /><Label>Critical for construction</Label></div>
          <div><Label>Remarks</Label><Textarea data-testid="eng-remarks-input" value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <div><Label>Back-date this entry (optional)</Label><Input type="date" data-testid="eng-entry-date-input" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="engineering-submit-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
